import { useState, useEffect, useCallback, useRef } from 'react'
import {
  postInventarioCompaniaProyectado,
} from 'src/services/motosSnopServices'
import {
  getCampoBasePorTipoDato,
  getTiposDatosPlanificacion,
  getValorCeldaPlanificacion,
} from 'src/constants/compraPlanificacionFilas'

const STORAGE_KEY_SEGMENTOS = 'resumenCompra_segmentosSeleccionados'
const STORAGE_KEY_ESTADOS = 'resumenCompra_estadosSeleccionados'
const STORAGE_KEY_PROTO_SEG = 'compra_prototipos_segundas'
const LOCAL_EVENT_PROTO_SEG = 'compraProtoSegChange'

const getStoredProtoSeg = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_PROTO_SEG)
    if (!stored) return { prototipos: false, segundas: false }
    const parsed = JSON.parse(stored)
    return {
      prototipos: Boolean(parsed?.prototipos),
      segundas: Boolean(parsed?.segundas),
    }
  } catch {
    return { prototipos: false, segundas: false }
  }
}

const getStoredArray = (key) => {
  try {
    const stored = localStorage.getItem(key)
    if (!stored) return []
    const parsed = JSON.parse(stored)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

export const useResumenCompraDetalle = (initialParams = {}, escenario = 'inicial_2025_6', tipoNegocio = 'TVS', setTipoNegocio = null) => {
  // Estados principales
  const [showAlert, setShowAlert] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')
  const [companiaSelected, setCompaniaSelected] = useState(tipoNegocio || initialParams.compania || 'TVS')
  const [companiasDisponibles] = useState(['TVS', 'VICTORY+', 'ELECTRIC', 'BLUE'])
  const [loadingCompanias] = useState(false)
  const [tablaData, setTablaData] = useState([])
  const [loadingTabla, setLoadingTabla] = useState(false)
  const [nivelServicio, setNivelServicio] = useState('')
  const [mesLlegada, setMesLlegada] = useState('')
  const [cargabilidad, setCargabilidad] = useState('')
  const [mesesColumnas, setMesesColumnas] = useState([])
  const [tipoPeriodo, setTipoPeriodo] = useState('mes')
  const [prototipos, setPrototipos] = useState(() => getStoredProtoSeg().prototipos)
  const [segundas, setSegundas] = useState(() => getStoredProtoSeg().segundas)
  const [sellInResumen, setSellInResumen] = useState({
    variacion_anual: null,
    sell_in_anio_anterior: null,
    sell_in_anio_actual: null,
  })
  const [kardexTecnoSuperActivo, setKardexTecnoSuperActivo] = useState(false)

  // Estados para filtros compartidos
  const [segmentosSeleccionados, setSegmentosSeleccionados] = useState(() => getStoredArray(STORAGE_KEY_SEGMENTOS))
  const [estadosSeleccionados, setEstadosSeleccionados] = useState(() => getStoredArray(STORAGE_KEY_ESTADOS))
  const filtrosActualesRef = useRef({
    tipoPeriodo: 'mes',
    segmentosSeleccionados: getStoredArray(STORAGE_KEY_SEGMENTOS),
    estadosSeleccionados: getStoredArray(STORAGE_KEY_ESTADOS),
    prototipos: getStoredProtoSeg().prototipos,
    segundas: getStoredProtoSeg().segundas,
  })
  
  // Opciones de tipo de período
  const tiposPeriodoDisponibles = [
    { value: 'mes', label: 'Mes' },
    { value: 'semana', label: 'Semana' }
  ]

  // Cargar filtros desde localStorage al inicializar
  useEffect(() => {
    try {
      const storedSegmentos = localStorage.getItem(STORAGE_KEY_SEGMENTOS)
      const storedEstados = localStorage.getItem(STORAGE_KEY_ESTADOS)
      
      if (storedSegmentos) {
        const segmentosParsed = JSON.parse(storedSegmentos)
        setSegmentosSeleccionados(Array.isArray(segmentosParsed) ? segmentosParsed : [])
      }
      
      if (storedEstados) {
        const estadosParsed = JSON.parse(storedEstados)
        setEstadosSeleccionados(Array.isArray(estadosParsed) ? estadosParsed : [])
      }
    } catch (error) {
      console.error('Error al cargar filtros desde localStorage:', error)
    }
  }, [])

  // Escuchar cambios en localStorage para sincronización bidireccional
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === STORAGE_KEY_SEGMENTOS && e.newValue) {
        try {
          const segmentosParsed = JSON.parse(e.newValue)
          setSegmentosSeleccionados(Array.isArray(segmentosParsed) ? segmentosParsed : [])
        } catch (error) {
          console.error('Error al parsear segmentos desde storage:', error)
        }
      }
      if (e.key === STORAGE_KEY_ESTADOS && e.newValue) {
        try {
          const estadosParsed = JSON.parse(e.newValue)
          setEstadosSeleccionados(Array.isArray(estadosParsed) ? estadosParsed : [])
        } catch (error) {
          console.error('Error al parsear estados desde storage:', error)
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)
    
    // También escuchar cambios locales usando un evento personalizado
    const handleLocalStorageChange = (e) => {
      if (e.detail.key === STORAGE_KEY_SEGMENTOS) {
        setSegmentosSeleccionados(Array.isArray(e.detail.value) ? e.detail.value : [])
      }
      if (e.detail.key === STORAGE_KEY_ESTADOS) {
        setEstadosSeleccionados(Array.isArray(e.detail.value) ? e.detail.value : [])
      }
    }

    window.addEventListener('localStorageChange', handleLocalStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('localStorageChange', handleLocalStorageChange)
    }
  }, [])

  // Actualizar companiaSelected cuando cambie tipoNegocio
  useEffect(() => {
    if (tipoNegocio) {
      setCompaniaSelected(tipoNegocio)
    }
  }, [tipoNegocio])

  useEffect(() => {
    filtrosActualesRef.current = {
      tipoPeriodo,
      segmentosSeleccionados,
      estadosSeleccionados,
      prototipos,
      segundas,
    }
  }, [tipoPeriodo, segmentosSeleccionados, estadosSeleccionados, prototipos, segundas])

  // Persistir y compartir estado de prototipos/segundas entre secciones
  useEffect(() => {
    try {
      const payload = { prototipos, segundas }
      localStorage.setItem(STORAGE_KEY_PROTO_SEG, JSON.stringify(payload))
      window.dispatchEvent(new CustomEvent(LOCAL_EVENT_PROTO_SEG, { detail: payload }))
    } catch {
      // Ignorar errores de storage
    }
  }, [prototipos, segundas])

  // Sincronizar cambios desde otras secciones/pestañas
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key !== STORAGE_KEY_PROTO_SEG || !e.newValue) return
      try {
        const parsed = JSON.parse(e.newValue)
        setPrototipos(Boolean(parsed?.prototipos))
        setSegundas(Boolean(parsed?.segundas))
      } catch {
        // Ignorar payload inválido
      }
    }

    const handleLocalEvent = (e) => {
      const detail = e?.detail || {}
      setPrototipos(Boolean(detail.prototipos))
      setSegundas(Boolean(detail.segundas))
    }

    window.addEventListener('storage', handleStorage)
    window.addEventListener(LOCAL_EVENT_PROTO_SEG, handleLocalEvent)
    return () => {
      window.removeEventListener('storage', handleStorage)
      window.removeEventListener(LOCAL_EVENT_PROTO_SEG, handleLocalEvent)
    }
  }, [])


  // Funciones de carga de datos
  const fetchTablaData = useCallback(async (compania, periodoOverride = null) => {
    setLoadingTabla(true)
    try {
      const filtros = filtrosActualesRef.current
      const periodoAUsar = periodoOverride || filtros.tipoPeriodo
      // Usar los filtros compartidos de segmentos y estados como listas
      const segmento = filtros.segmentosSeleccionados.length > 0 ? filtros.segmentosSeleccionados : []
      const estado = filtros.estadosSeleccionados.length > 0 ? filtros.estadosSeleccionados : []
      const prototiposFiltro = filtros.prototipos
      const segundasFiltro = filtros.segundas
      
      const response = await postInventarioCompaniaProyectado(
        compania,
        periodoAUsar,
        escenario,
        segmento,
        estado,
        prototiposFiltro,
        segundasFiltro,
      )
      
      const caracteristicasRespuesta = [
        ...(Array.isArray(response?.caracteristicas) ? response.caracteristicas : []),
        ...(Array.isArray(response?.success?.caracteristicas)
          ? response.success.caracteristicas
          : []),
      ]
      setKardexTecnoSuperActivo(
        caracteristicasRespuesta.includes('kardex_tecnosuper'),
      )

      // La API puede devolver success como objeto { detalle, variacion_anual, sell_in_anio_anterior, sell_in_anio_actual } o como array
        const rawSuccess = response?.success
        const esFormatoNuevo = rawSuccess && typeof rawSuccess === 'object' && !Array.isArray(rawSuccess) && rawSuccess.detalle
        const datosCompania = esFormatoNuevo ? rawSuccess.detalle : (Array.isArray(rawSuccess) ? rawSuccess : [])

        if (esFormatoNuevo) {
          setSellInResumen({
            variacion_anual: rawSuccess.variacion_anual ?? null,
            sell_in_anio_anterior: rawSuccess.sell_in_anio_anterior ?? null,
            sell_in_anio_actual: rawSuccess.sell_in_anio_actual ?? null,
          })
        } else {
          setSellInResumen({ variacion_anual: null, sell_in_anio_anterior: null, sell_in_anio_actual: null })
        }

        if (datosCompania.length === 0) {
          setTablaData([])
          setMesesColumnas([])
          setNivelServicio('')
          setMesLlegada('')
          setCargabilidad('')
          setSellInResumen({ variacion_anual: null, sell_in_anio_anterior: null, sell_in_anio_actual: null })
          return
        }

        // Extraer valores informativos similar al hook original
        // Extraer cargabilidad del primer elemento si existe
        if (datosCompania.length > 0 && datosCompania[0].cargabilidad !== undefined) {
          setCargabilidad(datosCompania[0].cargabilidad.toString())
        }
        
        // Extraer nivel de servicio y mes llegada del segundo elemento si existe (como en el hook original)
        if (datosCompania.length > 1) {
          const segundoElemento = datosCompania[1]
          
          // Extraer nivel de servicio (días inventario)
          if (segundoElemento.dias_inventario !== undefined) {
            const diasInventario = parseFloat(segundoElemento.dias_inventario)
            setNivelServicio(diasInventario.toFixed(2))
          }
          
          // Extraer mes de llegada
          // Si tiene fecha_disponible_para_facturacion, usarla
          if (segundoElemento.fecha_disponible_para_facturacion) {
            const fecha = new Date(segundoElemento.fecha_disponible_para_facturacion)
            const año = fecha.getFullYear()
            const mes = (fecha.getMonth() + 1).toString().padStart(2, '0')
            setMesLlegada(`${año}-${mes}-10`)
          } else if (segundoElemento.mes && segundoElemento.anio) {
            // Si no tiene fecha_disponible_para_facturacion, usar mes y anio
            const año = segundoElemento.anio
            const mes = segundoElemento.mes.toString().padStart(2, '0')
            setMesLlegada(`${año}-${mes}-10`)
          }
        } else if (datosCompania.length > 0) {
          // Si solo hay un elemento, usar ese
          const primerElemento = datosCompania[0]
          
          if (primerElemento.dias_inventario !== undefined) {
            const diasInventario = parseFloat(primerElemento.dias_inventario)
            setNivelServicio(diasInventario.toFixed(2))
          }
          
          if (primerElemento.mes && primerElemento.anio) {
            const año = primerElemento.anio
            const mes = primerElemento.mes.toString().padStart(2, '0')
            setMesLlegada(`${año}-${mes}-10`)
          }
        }

        const tiposDatos = getTiposDatosPlanificacion()
        const campoBasePorTipoDato = getCampoBasePorTipoDato()

        const aplicaConsignacionEnPeriodo = (d) => {
          const aplica = d?.aplica_consignacion
          return (
            aplica === 1 ||
            aplica === '1' ||
            aplica === true ||
            (aplica != null && !Number.isNaN(Number(aplica)) && Number(aplica) === 1)
          )
        }

        const tieneHighlightInventarioFinalUnPvReal = (d) => {
          if (!d) return false
          const base = aplicaConsignacionEnPeriodo(d)
            ? 'mb5b_inventario_con_consignacion'
            : 'mb5b_inventario_sin_consignacion'
          const valorPrototipos = parseFloat(d[`${base}_prototipos`])
          const valorSegundas = parseFloat(d[`${base}_segundas`])
          const prototiposMayorCero =
            prototiposFiltro && !Number.isNaN(valorPrototipos) && valorPrototipos > 0
          const segundasMayorCero = segundasFiltro && !Number.isNaN(valorSegundas) && valorSegundas > 0
          return prototiposMayorCero || segundasMayorCero
        }

        const tieneValorEspecial = (datosDelPeriodo, campoBase) => {
          if (!datosDelPeriodo || !campoBase) return false
          if (campoBase === 'inventario_final_un_pv_real') {
            return tieneHighlightInventarioFinalUnPvReal(datosDelPeriodo)
          }
          const valorPrototipos = parseFloat(datosDelPeriodo[`${campoBase}_prototipos`])
          const valorSegundas = parseFloat(datosDelPeriodo[`${campoBase}_segundas`])
          const prototiposMayorCero = prototiposFiltro && !Number.isNaN(valorPrototipos) && valorPrototipos > 0
          const segundasMayorCero = segundasFiltro && !Number.isNaN(valorSegundas) && valorSegundas > 0
          return prototiposMayorCero || segundasMayorCero
        }

        const datosPorPeriodo = {}
        const periodosDisponibles = new Set()

        datosCompania.forEach((item) => {
          let periodoKey
          if (periodoAUsar === 'semana') {
            // Para semanas, usar semana_iso y anio_iso si existen, sino usar mes y anio
            periodoKey = item.semana_iso && item.anio_iso 
              ? `${item.semana_iso}-${item.anio_iso}`
              : `${item.mes}-${item.anio}`
          } else {
            // Para meses, usar mes y anio
            periodoKey = `${item.mes}-${item.anio}`
          }
          datosPorPeriodo[periodoKey] = item
          periodosDisponibles.add(periodoKey)
        })

        const periodosOrdenados = Array.from(periodosDisponibles).sort((a, b) => {
          const [periodoA, añoA] = a.split('-').map(Number)
          const [periodoB, añoB] = b.split('-').map(Number)
          return añoA - añoB || periodoA - periodoB
        })

        const columnasGeneradas = periodosOrdenados.map((periodoKey) => {
          const [periodo, año] = periodoKey.split('-')
          
          if (periodoAUsar === 'semana') {
            const semanaFormateada = periodo.toString().padStart(2, '0')
            return `${semanaFormateada}-${año}`
          } 
            const nombresMeses = [
              'ene', 'feb', 'mar', 'abr', 'may', 'jun',
              'jul', 'ago', 'sep', 'oct', 'nov', 'dic',
            ]
            const nombreMes = nombresMeses[parseInt(periodo, 10) - 1]
            const añoCorto = año.toString().slice(-2)
            return `${nombreMes}-${añoCorto}`
        })

        const tablaDataFromAPI = tiposDatos.map((tipo, index) => {
          const fila = { id: index + 1, tipoDato: tipo }
          const camposResaltados = {}

          periodosOrdenados.forEach((periodoKey, idx) => {
            const nombreColumna = columnasGeneradas[idx]
            const datosDelPeriodo = datosPorPeriodo[periodoKey]

            if (datosDelPeriodo) {
              const campoBase = campoBasePorTipoDato[tipo]
              camposResaltados[nombreColumna] = tieneValorEspecial(datosDelPeriodo, campoBase)
              fila[nombreColumna] = getValorCeldaPlanificacion(tipo, datosDelPeriodo)
            }
          })

          fila.__highlightFlags = camposResaltados
          return fila
        })

        setMesesColumnas(columnasGeneradas)
        setTablaData(tablaDataFromAPI)
    } catch (error) {
      console.error('Error al cargar datos de la tabla:', error)
      setTablaData([])
      setMesesColumnas([])
      setNivelServicio('')
      setMesLlegada('')
      setCargabilidad('')
      setSellInResumen({ variacion_anual: null, sell_in_anio_anterior: null, sell_in_anio_actual: null })
      setKardexTecnoSuperActivo(false)
    } finally {
      setLoadingTabla(false)
    }
  }, [escenario])

  // Cargar datos cuando se inicializa con parámetros
  useEffect(() => {
    if (initialParams?.compania) {
      setCompaniaSelected(initialParams.compania)
      fetchTablaData(initialParams.compania)
    } else if (companiaSelected) {
      fetchTablaData(companiaSelected)
    }
  }, [initialParams?.compania, fetchTablaData])

  // Handlers
  const handleTipoPeriodoChange = (e) => {
    const selectedTipoPeriodo = e.target.value
    setTipoPeriodo(selectedTipoPeriodo)
  }

  const handleCompaniaChange = (e) => {
    const selectedCompania = e.target.value
    setCompaniaSelected(selectedCompania)
    if (setTipoNegocio && selectedCompania) {
      setTipoNegocio(selectedCompania)
    }
    if (!selectedCompania) {
      setTablaData([])
    }
  }

  const handleNextCompania = () => {
    const currentIndex = companiasDisponibles.findIndex(c => c === companiaSelected)
    if (currentIndex < companiasDisponibles.length - 1) {
      const selectedCompania = companiasDisponibles[currentIndex + 1]
      setCompaniaSelected(selectedCompania)
      if (setTipoNegocio) setTipoNegocio(selectedCompania)
    }
  }

  const handlePreviousCompania = () => {
    const currentIndex = companiasDisponibles.findIndex(c => c === companiaSelected)
    if (currentIndex > 0) {
      const selectedCompania = companiasDisponibles[currentIndex - 1]
      setCompaniaSelected(selectedCompania)
      if (setTipoNegocio) setTipoNegocio(selectedCompania)
    }
  }

  const handleAplicarFiltros = () => {
    if (companiaSelected) {
      fetchTablaData(companiaSelected)
    }
  }

  const handleCloseAlert = () => {
    setShowAlert(false)
  }

  return {
    // Estados
    showAlert,
    alertMessage,
    companiaSelected,
    companiasDisponibles,
    loadingCompanias,
    tablaData,
    loadingTabla,
    nivelServicio,
    mesLlegada,
    cargabilidad,
    mesesColumnas,
    tipoPeriodo,
    prototipos,
    segundas,
    tiposPeriodoDisponibles,
    segmentosSeleccionados,
    estadosSeleccionados,
    sellInResumen,
    kardexTecnoSuperActivo,
    
    // Setters
    setCompaniaSelected,
    setTipoPeriodo,
    setSegmentosSeleccionados,
    setEstadosSeleccionados,
    setPrototipos,
    setSegundas,
    
    // Handlers
    handleCompaniaChange,
    handleTipoPeriodoChange,
    handleAplicarFiltros,
    handleCloseAlert,
    handleNextCompania,
    handlePreviousCompania,
    
    // Funciones para recargar datos
    refetchTablaData: () => fetchTablaData(companiaSelected),
  }
}
